/*
  Что должник видит: сумма, из чего она сложилась, чем заплатить, график.

  Только показ. Ни одна из этих частей ничего не считает и никуда не пишет —
  суммы и тексты приходят с сервера (Глава 8.7). Посчитать взнос или итог на
  клиенте было бы легко, и это был бы второй ответ на вопрос «сколько платить»,
  который однажды разойдётся с первым.
*/

import { useState } from 'react'

import { Notice } from './Common.jsx'
import PageBar from './PageBar.jsx'
import usePaging from './usePaging.js'
import { date, days, money, monthTitle } from '../api/format.js'

function Period({ since, until }) {
  return since === until ? (
    <>{date(since)}</>
  ) : (
    <>
      {date(since)} - {date(until)}
    </>
  )
}

/*
  Расчёт пеней построчно - тот же, что уходит приложением к заявлению в суд.

  Формула приходит с сервера готовой строкой: собирать её здесь значило бы
  завести второй ответ на вопрос «откуда столько», и он разошёлся бы с бумагой,
  которую человек получит из суда.
*/
function PenaltySteps({ steps }) {
  // 21.10.2026: ступеней просрочки по долгу в несколько лет - десятки; общая панель
  // страниц. Хук стоит до раннего возврата: порядок хуков не должен зависеть от пустоты.
  const { rows, bar } = usePaging(steps)
  if (!steps.length) return null
  // Шапки у таблицы нет намеренно: страница должника - лист в 720 пикселей, и
  // таблица с `thead` разворачивается в карточку по строке на ячейку (осмотр
  // 15.09.2026). Здесь работает приём расшифровки: пара «за что - сколько»
  // остаётся в строке, а период, дни и формула уходят под неё мелким.
  return (
    <>
      <table className="table table--lines">
        <tbody>
          {rows.map((step) => (
            // Строка - ступень по долгу месяца, без услуги (06.10.2026): пени
            // считаются от долга месяца целиком, как в расчёте юриста.
            <tr key={`${step.period}-${step.starts}`}>
              <td>{monthTitle(step.period.slice(0, 7))}</td>
              <td className="muted">
                <Period since={step.starts} until={step.ends} />
                {`, ${days(step.days)}: ${step.formula}`}
              </td>
              <td className="table__num">{money(step.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <PageBar {...bar} what="строк расчёта" />
    </>
  )
}

/*
  Помесячная расшифровка (21.10.2026): строк «месяц - услуга» у долга в несколько лет
  больше сотни, поэтому таблица разбита общей панелью страниц.
*/
function Monthly({ lines }) {
  const { rows, bar } = usePaging(lines)
  return (
    <>
      <div className="table-scroll">
        <table className="table table--lines">
          <tbody>
            {rows.map((line) => (
              <tr key={`${line.period}-${line.service}`}>
                <td>{date(line.period)}</td>
                <td>{line.service}</td>
                <td className="table__num">{money(line.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PageBar {...bar} what="строк" />
    </>
  )
}

export function Breakdown({ breakdown, penaltySteps = [] }) {
  const [detailed, setDetailed] = useState(false)
  const [penaltyShown, setPenaltyShown] = useState(false)
  return (
    <div className="card">
      <div className="section__title">Из чего складывается</div>
      <table className="table table--lines">
        <tbody>
          {breakdown.services.map((line) => (
            <tr key={line.service}>
              <td>{line.service}</td>
              <td className="muted">
                <Period since={line.since} until={line.until} />
              </td>
              <td className="table__num">{money(line.amount)}</td>
            </tr>
          ))}
          {Number(breakdown.penalty) > 0 ? (
            <tr>
              <td>Пени за просрочку</td>
              <td className="muted">начислены по день расчёта</td>
              <td className="table__num">{money(breakdown.penalty)}</td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <div className="row">
        <button
          type="button"
          className="button button--quiet"
          onClick={() => setDetailed(!detailed)}
        >
          {detailed ? 'Свернуть' : 'Показать помесячно'}
        </button>
        {penaltySteps.length ? (
          <button
            type="button"
            className="button button--quiet"
            onClick={() => setPenaltyShown(!penaltyShown)}
          >
            {penaltyShown ? 'Свернуть расчёт пеней' : 'Как посчитаны пени'}
          </button>
        ) : null}
      </div>

      {detailed ? <Monthly lines={breakdown.monthly} /> : null}

      {penaltyShown ? <PenaltySteps steps={penaltySteps} /> : null}
    </div>
  )
}

export function Payment({ payment, total }) {
  if (!payment.is_ready) {
    // Пустой экран объясняет, чего не хватает и что делать (§4.5.4).
    return (
      <div className="card">
        <div className="section__title">Оплата</div>
        <Notice kind="warn">
          <div className="notice__what">
            Оплатить на этой странице пока нельзя: организация не заполнила{' '}
            {payment.missing.join(', ')}.
          </div>
          <div className="notice__do">
            Оплатите долг привычным способом - в приложении банка, на почте или в кассе
            организации, указав лицевой счёт.
          </div>
        </Notice>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="section__title">Оплатить {money(total)}</div>
      {payment.code_image ? (
        <div className="qr">
          <img
            className="qr__image"
            src={payment.code_image}
            alt="Код для оплаты в приложении банка"
          />
          {/* Видна только в версии для слабовидящих с выключенными
              изображениями - вместо самой картинки (02.10.2026). Читалке
              экрана не нужна: у картинки есть свой `alt`. */}
          <div className="qr__alt" aria-hidden="true">
            Здесь код для оплаты в приложении банка
          </div>
          <div className="qr__hint">
            Наведите камеру телефона или откройте код в приложении банка: получатель, сумма и
            назначение подставятся сами. Комиссию определяет ваш банк.
          </div>
        </div>
      ) : null}
      <div className="log">
        <div className="log__row">
          <span className="log__when">Получатель</span>
          <span>
            {payment.payee}
            {payment.payee_inn ? `, ИНН ${payment.payee_inn}` : ''}
          </span>
        </div>
        <div className="log__row">
          <span className="log__when">Реквизиты</span>
          <span>{payment.bank_details}</span>
        </div>
        <div className="log__row">
          <span className="log__when">Назначение</span>
          <span>{payment.purpose}</span>
        </div>
      </div>
      <div className="muted">
        Платёж по частям тоже засчитывается: любая внесённая сумма уменьшает долг.
      </div>
    </div>
  )
}

export function Schedule({ schedule }) {
  // 21.10.2026: график рассрочки - до трёх лет помесячно, общая панель страниц.
  const { rows, bar } = usePaging(schedule)
  if (!schedule.length) return null
  return (
    <div className="card">
      <div className="section__title">Ваш график рассрочки</div>
      <table className="table table--lines">
        <tbody>
          {rows.map((item) => (
            <tr key={item.number}>
              <td>Платёж {item.number}</td>
              <td>{date(item.due_on)}</td>
              <td className="table__num">{money(item.amount)}</td>
              <td className="muted">{item.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <PageBar {...bar} what="платежей" />
    </div>
  )
}
